import {
  Button,
  CodeSnippet,
  InlineNotification,
  Modal,
  NumberInput,
  SkeletonText,
  Stack,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  TextArea,
  TextInput,
  Tile,
} from '@carbon/react';
import { Password, Unlink } from '@carbon/icons-react';
import { asProblemMessage } from './problem';
import { connectionKey, hasPermission, useAdmin, useFormat, type RecordValues } from '@pos/admin-ui';
import type { PairingCodeDto, StoreConnectionDto } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ConnectionStatus } from './ConnectionStatus';
import './sync.scss';

async function load(storeId: string): Promise<StoreConnectionDto> {
  const { data, error } = await hoApi.GET('/api/v1/sync/stores/{storeId}/connection', {
    params: { path: { storeId } },
  });
  if (!data) throw error;
  return data;
}

/**
 * A store's connection to HO (ADR 0014): the internet addresses HO accepts its calls from, its server on the store
 * LAN, pairing with a one-time code, revoking its key, and what its heartbeats report.
 */
export function StoreConnectionTab({ store }: { store: RecordValues }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.sync.manage');
  const storeId = String(store.id);
  const queryClient = useQueryClient();
  const connection = useQuery({
    queryKey: [...connectionKey, storeId],
    queryFn: () => load(storeId),
    refetchInterval: 30_000,
  });
  const [edits, setEdits] = useState<{ publicIps?: string; lanHost?: string; lanPort?: number }>({});
  const [code, setCode] = useState<PairingCodeDto>();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const refresh = (next: StoreConnectionDto) => {
    queryClient.setQueryData([...connectionKey, storeId], next);
    void queryClient.invalidateQueries({ queryKey: connectionKey });
  };

  const save = useMutation({
    mutationFn: async () => {
      const current = connection.data;
      const body = {
        publicIps: (edits.publicIps ?? current?.publicIps.join('\n') ?? '').split(/[\s,]+/).filter(Boolean),
        lanHost: edits.lanHost ?? current?.lanHost ?? null,
        lanPort: edits.lanPort ?? Number(current?.lanPort ?? 5100),
      };
      const { data, error } = await hoApi.PUT('/api/v1/sync/stores/{storeId}/connection', {
        params: { path: { storeId } },
        body,
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setEdits({});
      refresh(next);
    },
  });

  const pair = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/sync/stores/{storeId}/pairing-code', {
        params: { path: { storeId } },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setCode(next);
      void connection.refetch();
    },
  });

  const revoke = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/sync/stores/{storeId}/revoke', {
        params: { path: { storeId } },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setConfirmRevoke(false);
      setCode(undefined);
      refresh(next);
    },
  });

  if (connection.isLoading) return <SkeletonText paragraph lineCount={3} aria-label={t('common.loading')} />;
  if (!connection.data)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={asProblemMessage(t, connection.error)}
      />
    );
  const c = connection.data;
  const ips = edits.publicIps ?? c.publicIps.join('\n');
  const when = (value: string | null | undefined) => (value ? format.dateTime(value) : t('admin.sync.never'));

  return (
    <Stack gap={7} className="pos-sync-connection">
      <ConnectionStatus connection={c} />

      <Tile>
        <Stack gap={5}>
          <h2 className="pos-sync-connection__heading">{t('admin.sync.pairing')}</h2>
          <p>{t('admin.sync.pairingHint')}</p>
          {c.apiKeyPrefix && c.keyIssuedAt && (
            <p>{t('admin.sync.keyIssued', { prefix: c.apiKeyPrefix, time: format.dateTime(c.keyIssuedAt) })}</p>
          )}
          {c.revokedAt && c.status === 'revoked' && (
            <p>{t('admin.sync.revokedAt', { time: format.dateTime(c.revokedAt) })}</p>
          )}
          {code && (
            <div className="pos-sync-connection__code" role="status">
              <span className="pos-sync-connection__code-label">{t('admin.sync.code')}</span>
              <CodeSnippet
                type="single"
                feedback={t('admin.sync.copied')}
                copyButtonDescription={t('admin.sync.copyCode')}
              >
                {code.code}
              </CodeSnippet>
              <span>{t('admin.sync.codeValid', { time: format.dateTime(code.expiresAt) })}</span>
            </div>
          )}
          {pair.error && (
            <InlineNotification kind="error" lowContrast hideCloseButton title={asProblemMessage(t, pair.error)} />
          )}
          {canManage && (
            <div className="pos-sync-connection__actions">
              <Button kind="primary" renderIcon={Password} disabled={pair.isPending} onClick={() => pair.mutate()}>
                {t('admin.sync.createCode')}
              </Button>
              {c.status === 'paired' && (
                <Button kind="danger--tertiary" renderIcon={Unlink} onClick={() => setConfirmRevoke(true)}>
                  {t('admin.sync.revoke')}
                </Button>
              )}
            </div>
          )}
          {canManage && c.status === 'paired' && (
            <p className="pos-sync-connection__hint">{t('admin.sync.repairWarning')}</p>
          )}
        </Stack>
      </Tile>

      <Tile>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Stack gap={5}>
            <h2 className="pos-sync-connection__heading">{t('admin.sync.network')}</h2>
            <TextArea
              id="sync-public-ips"
              labelText={t('admin.sync.publicIps')}
              helperText={t('admin.sync.publicIpsHelp')}
              rows={3}
              value={ips}
              readOnly={!canManage}
              onChange={(e) => setEdits((v) => ({ ...v, publicIps: e.target.value }))}
            />
            {ips.trim() === '' && (
              <InlineNotification kind="warning" lowContrast hideCloseButton title={t('admin.sync.anyAddress')} />
            )}
            <div className="pos-sync-connection__row">
              <TextInput
                id="sync-lan-host"
                labelText={t('admin.sync.lanHost')}
                helperText={t('admin.sync.lanHostHelp')}
                value={edits.lanHost ?? c.lanHost ?? ''}
                readOnly={!canManage}
                onChange={(e) => setEdits((v) => ({ ...v, lanHost: e.target.value }))}
              />
              <NumberInput
                id="sync-lan-port"
                label={t('admin.sync.lanPort')}
                min={1}
                max={65535}
                hideSteppers
                value={edits.lanPort ?? Number(c.lanPort)}
                readOnly={!canManage}
                onChange={(_, { value }) => setEdits((v) => ({ ...v, lanPort: Number(value) }))}
              />
            </div>
            {save.error && (
              <InlineNotification kind="error" lowContrast hideCloseButton title={asProblemMessage(t, save.error)} />
            )}
            {save.isSuccess && (
              <InlineNotification
                kind="success"
                role="status"
                lowContrast
                title={t('admin.sync.saved')}
                onClose={() => save.reset()}
              />
            )}
            {canManage && (
              <div>
                <Button type="submit" kind="primary" disabled={save.isPending}>
                  {t('admin.sync.saveNetwork')}
                </Button>
              </div>
            )}
          </Stack>
        </form>
      </Tile>

      <Tile>
        <Stack gap={5}>
          <h2 className="pos-sync-connection__heading">{t('admin.sync.health')}</h2>
          <StructuredListWrapper isCondensed aria-label={t('admin.sync.health')}>
            <StructuredListBody>
              {[
                [t('admin.sync.lastHeartbeat'), when(c.lastHeartbeatAt)],
                [t('admin.sync.lastRelay'), when(c.lastRelayAt ?? null)],
                [t('admin.sync.appVersion'), c.appVersion ?? '—'],
                [t('admin.sync.outboxDepth'), c.outboxDepth == null ? '—' : format.number(c.outboxDepth)],
                [t('admin.sync.oldestPending'), c.oldestPendingAt ? format.dateTime(c.oldestPendingAt) : '—'],
                [t('admin.sync.lastPush'), when(c.lastPushAt)],
                [t('admin.sync.lastPull'), format.number(c.lastPullSequence)],
                [t('admin.sync.lastSeenIp'), c.lastSeenIp ?? '—'],
                [t('admin.sync.lanAddresses'), c.lanAddresses.length ? c.lanAddresses.join(', ') : '—'],
                [
                  t('admin.sync.heldChanges'),
                  c.heldChanges
                    ? t('admin.sync.heldChangesValue', {
                        count: Number(c.heldChanges.count),
                        types: c.heldChanges.entityTypes.join(', '),
                        since: c.heldChanges.oldestAt ? format.dateTime(c.heldChanges.oldestAt) : '—',
                      })
                    : '—',
                ],
                [t('admin.sync.lastError'), c.lastError ?? '—'],
              ].map(([label, value]) => (
                <StructuredListRow key={label}>
                  <StructuredListCell noWrap>{label}</StructuredListCell>
                  <StructuredListCell>{value}</StructuredListCell>
                </StructuredListRow>
              ))}
            </StructuredListBody>
          </StructuredListWrapper>
        </Stack>
      </Tile>

      {confirmRevoke && (
        <Modal
          open
          danger
          modalHeading={t('admin.sync.revokeTitle')}
          modalLabel={String(store.name ?? '')}
          closeButtonLabel={t('carbon.close')}
          primaryButtonText={t('admin.sync.revoke')}
          secondaryButtonText={t('common.cancel')}
          primaryButtonDisabled={revoke.isPending}
          onRequestClose={() => setConfirmRevoke(false)}
          onRequestSubmit={() => revoke.mutate()}
        >
          <p>{t('admin.sync.revokeBody')}</p>
          {revoke.error && (
            <InlineNotification kind="error" lowContrast hideCloseButton title={asProblemMessage(t, revoke.error)} />
          )}
        </Modal>
      )}
    </Stack>
  );
}
